import { plainToInstance } from "class-transformer"
import moment from "moment-timezone"
import * as PapaParse from "papaparse"

import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import {
    EraUploadFileUploadStatus,
    INTEGRATIONS_CONFIG,
} from "../config/integrations.config"
import { RawEraUploadCsvRowDTO } from "../dto/rawEraUploadCsvRow.dto"
import type {
    IEraUploadBatchReviewState,
    IEraUploadBatchSummary,
    IEraUploadCompletionState,
    IEraUploadCsvPreviewResult,
    IEraUploadCsvValidationResult,
    IEraUploadFileReviewState,
    IEraUploadRowDisplayColumnMapping,
    IEraUploadX12SegmentPresence,
    IEraUploadX12ValidationResult,
} from "../types/integrations.types"
import { CsvHelper } from "./csv.helper"
import { UtilHelper } from "./util.helper"

export class EraUploadHelper {
    /**
     * Human-readable max file size for validation error messages.
     */
    private static formatMaxFileSize(): string {
        return this.formatFileSize(INTEGRATIONS_CONFIG.eraUpload.maxFileSizeBytes)
    }

    /**
     * Format the next ingest run banner label for the Uploaded Files UI.
     */
    static formatNextIngestRunLabel(): string {
        const { dailyIngestRunHourEt, dailyIngestTimezone } =
            INTEGRATIONS_CONFIG.eraUpload

        const nowEt = moment.tz(dailyIngestTimezone)
        let nextRun = nowEt
            .clone()
            .startOf("day")
            .hour(dailyIngestRunHourEt)
            .minute(0)
            .second(0)
            .millisecond(0)

        if (!nextRun.isAfter(nowEt)) {
            nextRun = nextRun.add(1, "day")
        }

        const runDay = nextRun.format("YYYY-MM-DD")
        const today = nowEt.format("YYYY-MM-DD")
        const tomorrow = nowEt.clone().add(1, "day").format("YYYY-MM-DD")

        const dayLabel =
            runDay === today
                ? "Today"
                : runDay === tomorrow
                  ? "Tomorrow"
                  : nextRun.format("MMM D")

        return `Next run: ${dayLabel} • ${nextRun.format("h:mm A")} ET`
    }

    /**
     * Whether a file is within the configured per-file size limit.
     */
    private static isFileWithinSizeLimit(file: File): boolean {
        return file.size <= INTEGRATIONS_CONFIG.eraUpload.maxFileSizeBytes
    }

    /**
     * Stable id for a file row in a batch review session.
     */
    private static createFileId(): string {
        return UtilHelper.generateUniqueVarchar(12, false)
    }

    /**
     * Create a new batch review session from validated file rows.
     */
    static createBatchReviewState(
        fileFormat: OneVerifyEraUploadFileFormat,
        files: IEraUploadFileReviewState[],
    ): IEraUploadBatchReviewState {
        const batch: IEraUploadBatchReviewState = {
            batchId: UtilHelper.generateUniqueVarchar(12, false),
            batchTimestamp: Date.now(),
            fileFormat,
            files: [],
        }

        return {
            ...batch,
            files: this.withBatchFileNames(files),
        }
    }

    /**
     * Assign validated files into a batch (batch metadata lives on the batch object).
     */
    private static withBatchFileNames(
        files: IEraUploadFileReviewState[],
    ): IEraUploadFileReviewState[] {
        return files.map(file => {
            return {
                ...file,
                filename: file.file.name,
            }
        })
    }

    /**
     * Append validated files to an existing batch (preserves batch id and timestamp).
     */
    static appendFilesToBatch(
        batch: IEraUploadBatchReviewState,
        files: IEraUploadFileReviewState[],
    ): IEraUploadBatchReviewState {
        return {
            ...batch,
            files: [
                ...batch.files,
                ...this.withBatchFileNames(files),
            ],
        }
    }

    /**
     * Replace a single file in the batch by id.
     */
    static replaceFileInBatch(
        batch: IEraUploadBatchReviewState,
        fileId: string,
        replacement: IEraUploadFileReviewState,
    ): IEraUploadBatchReviewState {
        const fileIndex = batch.files.findIndex(file => file.id === fileId)

        if (fileIndex === -1) {
            return batch
        }

        const files = [...batch.files]

        files.splice(fileIndex, 1, {
            ...replacement,
            filename: replacement.file.name,
        })

        return {
            ...batch,
            files,
        }
    }

    /**
     * Remove a file from the batch by id.
     */
    static removeFileFromBatch(
        batch: IEraUploadBatchReviewState,
        fileId: string,
    ): IEraUploadBatchReviewState {
        return {
            ...batch,
            files: batch.files.filter(file => file.id !== fileId),
        }
    }

    /**
     * Update one file's upload-related fields in the batch.
     */
    static updateFileInBatch(
        batch: IEraUploadBatchReviewState,
        fileId: string,
        update: Partial<
            Pick<IEraUploadFileReviewState, "uploadStatus" | "uploadError">
        >,
    ): IEraUploadBatchReviewState {
        return {
            ...batch,
            files: batch.files.map(file => {
                if (file.id !== fileId) {
                    return file
                }

                return {
                    ...file,
                    ...update,
                }
            }),
        }
    }

    /**
     * Valid files that still need uploading (pending or previously failed).
     */
    static getUploadableFiles(
        batch: IEraUploadBatchReviewState,
    ): IEraUploadFileReviewState[] {
        return batch.files.filter(file => {
            if (!file.isValid) {
                return false
            }

            return (
                file.uploadStatus === EraUploadFileUploadStatus.pending ||
                file.uploadStatus === EraUploadFileUploadStatus.failed
            )
        })
    }

    /**
     * Whether every valid file in the batch has uploaded successfully.
     */
    static areAllValidFilesUploaded(
        batch: IEraUploadBatchReviewState,
    ): boolean {
        const validFiles = batch.files.filter(file => file.isValid)

        if (!validFiles.length) {
            return false
        }

        return validFiles.every(
            file => file.uploadStatus === EraUploadFileUploadStatus.succeeded,
        )
    }

    /**
     * Build completion summary after a fully successful batch upload.
     */
    static buildCompletionState(
        batch: IEraUploadBatchReviewState,
    ): IEraUploadCompletionState {
        const uploadedFiles = batch.files.filter(
            file =>
                file.isValid &&
                file.uploadStatus === EraUploadFileUploadStatus.succeeded,
        )

        const totalCsvRowCount = uploadedFiles.reduce((sum, file) => {
            return sum + file.totalRowCount
        }, 0)

        return {
            fileFormat: batch.fileFormat,
            uploadedFileCount: uploadedFiles.length,
            totalCsvRowCount,
            fileNames: uploadedFiles.map(file => file.file.name),
            completedAt: Date.now(),
        }
    }

    /**
     * Primary heading for the post-upload confirmation screen.
     */
    static getUploadCompleteTitle(state: IEraUploadCompletionState): string {
        if (state.uploadedFileCount === 1) {
            return "Upload complete"
        }

        return `${state.uploadedFileCount} files uploaded`
    }

    /**
     * Body copy for the post-upload confirmation screen.
     */
    static getUploadCompleteDescription(
        state: IEraUploadCompletionState,
    ): string {
        const isX12 = state.fileFormat === OneVerifyEraUploadFileFormat.x12

        if (state.uploadedFileCount === 1) {
            return isX12
                ? "Your X12 835 remittance file was received and is queued for processing."
                : `Your ERA CSV file was received (${state.totalCsvRowCount.toLocaleString()} rows) and is queued for processing.`
        }

        if (isX12) {
            return `Your ${state.uploadedFileCount} X12 835 remittance files were received and are queued for processing.`
        }

        return `Your ${state.uploadedFileCount} ERA CSV files were received (${state.totalCsvRowCount.toLocaleString()} total rows) and are queued for processing.`
    }

    /**
     * Aggregate counts for review header and floating controls.
     */
    static getBatchSummary(
        batch: IEraUploadBatchReviewState,
    ): IEraUploadBatchSummary {
        const validFiles = batch.files.filter(file => file.isValid)
        const invalidFiles = batch.files.filter(file => !file.isValid)
        const uploadableFiles = this.getUploadableFiles(batch)
        const succeededFiles = batch.files.filter(
            file => file.uploadStatus === EraUploadFileUploadStatus.succeeded,
        )
        const failedUploadFiles = batch.files.filter(
            file => file.uploadStatus === EraUploadFileUploadStatus.failed,
        )

        const totalCsvRowCount = validFiles.reduce((sum, file) => {
            return sum + file.totalRowCount
        }, 0)

        const previewCsvRowCount = validFiles.reduce((sum, file) => {
            return sum + file.rows.length
        }, 0)

        return {
            totalFileCount: batch.files.length,
            validFileCount: validFiles.length,
            invalidFileCount: invalidFiles.length,
            uploadableFileCount: uploadableFiles.length,
            succeededFileCount: succeededFiles.length,
            failedUploadFileCount: failedUploadFiles.length,
            totalCsvRowCount,
            previewCsvRowCount,
            isCsvPreviewTruncated: validFiles.some(file => {
                return file.totalRowCount > file.rows.length
            }),
        }
    }

    /**
     * Validate a single file and return a review row (valid or invalid).
     */
    private static async validateFileForReview(
        file: File,
        acceptedFileFormat: OneVerifyEraUploadFileFormat,
        options?: { isCsvUploadEnabled?: boolean },
    ): Promise<IEraUploadFileReviewState> {
        const id = this.createFileId()

        const base: IEraUploadFileReviewState = {
            id,
            fileFormat: acceptedFileFormat,
            file,
            filename: file.name,
            rows: [],
            totalRowCount: 0,
            isValid: false,
            uploadStatus: EraUploadFileUploadStatus.pending,
        }

        try {
            if (!this.isFileWithinSizeLimit(file)) {
                return {
                    ...base,
                    validationError: `File exceeds the ${this.formatMaxFileSize()} limit.`,
                }
            }

            const isCsvUploadEnabled =
                options?.isCsvUploadEnabled ??
                acceptedFileFormat === OneVerifyEraUploadFileFormat.csv

            const isCsvFormat =
                acceptedFileFormat === OneVerifyEraUploadFileFormat.csv

            const x12ExtensionsLabel =
                COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()

            const fileFormat = await this.detectFileFormat(file, {
                isCsvUploadEnabled,
            })

            if (!fileFormat || fileFormat !== acceptedFileFormat) {
                return {
                    ...base,
                    validationError: isCsvFormat
                        ? "Unsupported file type. Upload a .csv spreadsheet."
                        : `Unsupported file type. Upload an X12 835 file (${x12ExtensionsLabel}).`,
                }
            }

            if (fileFormat === OneVerifyEraUploadFileFormat.x12) {
                const validation = await this.validateX12File(file)

                if (validation.isValid) {
                    return {
                        ...base,
                        isValid: true,
                    }
                }

                return {
                    ...base,
                    validationError:
                        validation.errorMessage ??
                        "This file could not be validated as X12 835.",
                }
            }

            const preview = await this.parseCsvFilePreview(file)

            if (preview.validation.isValid && preview.totalRowCount) {
                return {
                    ...base,
                    rows: preview.rows,
                    totalRowCount: preview.totalRowCount,
                    isValid: true,
                }
            }

            return {
                ...base,
                validationError:
                    preview.validation.errorMessage ??
                    "This file could not be validated. Check the template and try again.",
            }
        } catch (error) {
            return {
                ...base,
                validationError:
                    "An unexpected error occurred during file validation.",
            }
        }
    }

    /**
     * Validate multiple files with bounded concurrency.
     */
    static async validateFilesForReview(
        files: File[],
        acceptedFileFormat: OneVerifyEraUploadFileFormat,
        options?: {
            isCsvUploadEnabled?: boolean
            onProgress?: (completed: number, total: number) => void
        },
    ): Promise<IEraUploadFileReviewState[]> {
        const results: IEraUploadFileReviewState[] = []

        const concurrency = INTEGRATIONS_CONFIG.eraUpload.validationConcurrency

        for (let index = 0; index < files.length; index += concurrency) {
            const chunk = files.slice(index, index + concurrency)

            const chunkResults = await Promise.all(
                chunk.map(file => {
                    return this.validateFileForReview(
                        file,
                        acceptedFileFormat,
                        options,
                    )
                }),
            )

            results.push(...chunkResults)

            options?.onProgress?.(
                Math.min(index + chunk.length, files.length),
                files.length,
            )
        }

        return results
    }

    /**
     * Read a review-table cell value from row data using the column mapping path.
     */
    static getRowDisplayValue(
        mapping: IEraUploadRowDisplayColumnMapping,
        row: RawEraUploadCsvRowDTO | null,
    ): string {
        const value = CsvHelper.getValueByPath(row, mapping.valuePath, "")

        if (value === "" || value === undefined || value === null) {
            return ""
        }

        return String(value)
    }

    /**
     * Infer CSV vs X12 from extension and, for .txt, a short content sniff (ISA vs CSV headers).
     */
    private static async detectFileFormat(
        file: File,
        options?: { isCsvUploadEnabled?: boolean },
    ): Promise<OneVerifyEraUploadFileFormat | null> {
        const isCsvUploadEnabled = options?.isCsvUploadEnabled ?? true

        const extension = this.getFileExtension(file.name)

        if (
            isCsvUploadEnabled &&
            extension ===
                COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.allowedCsvExtension
        ) {
            return OneVerifyEraUploadFileFormat.csv
        }

        const isAllowedX12Extension =
            !!extension &&
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.isAllowedX12Extension(extension)

        if (!isAllowedX12Extension) {
            return null
        }

        if (
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.isX12PlainTextExtension(
                extension,
            )
        ) {
            return this.detectTxtFileFormat(file, isCsvUploadEnabled)
        }

        return OneVerifyEraUploadFileFormat.x12
    }

    /**
     * Format a byte count for display (B, KB, or MB).
     */
    static formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    /**
     * Confirm the file is an allowed X12 type and contains required ISA/GS/ST segments.
     */
    private static async validateX12File(file: File) {
        const format = await this.detectFileFormat(file)

        if (format !== OneVerifyEraUploadFileFormat.x12) {
            return {
                isValid: false as const,
                errorMessage: `Unsupported file type. Use ${COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()}, or an X12 .txt file.`,
            }
        }

        const validation = await this.validateX12Structure(file)

        if (!validation.isValid) {
            return {
                isValid: false as const,
                errorMessage:
                    validation.errorMessage ??
                    "This file could not be validated as X12 835.",
            }
        }

        return {
            isValid: true as const,
        }
    }

    /**
     * Validate the file and return a capped preview of parsed rows plus total row count.
     */
    private static async parseCsvFilePreview(
        file: File,
    ): Promise<IEraUploadCsvPreviewResult> {
        const validation = await this.validateCsvFile(file)

        if (!validation.isValid) {
            return {
                rows: [],
                totalRowCount: 0,
                validation,
            }
        }

        const rows = await this.parseCsvPreviewFromFile(
            file,
            INTEGRATIONS_CONFIG.eraUpload.maxPreviewTableRows,
        )

        return {
            rows,
            totalRowCount: validation.rowCount,
            validation,
        }
    }

    /**
     * Validate extension, required/known headers, and that at least one data row exists.
     */
    private static async validateCsvFile(
        file: File,
    ): Promise<IEraUploadCsvValidationResult> {
        const name = file.name.toLowerCase()

        if (
            !name.endsWith(".csv") &&
            file.type !== "text/csv" &&
            file.type !== "application/csv"
        ) {
            return {
                isValid: false,
                headers: [],
                rowCount: 0,
                errorMessage:
                    "Only .csv files are supported for spreadsheet upload.",
            }
        }

        const fileHeaders = await this.readCsvFileHeaders(file)

        if (!fileHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: "Could not read column headers from the file.",
            }
        }

        const missingHeaders = this.getMissingRequiredCsvHeaders(fileHeaders)

        if (missingHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: `Missing required column(s): ${missingHeaders.join(", ")}. Download the template and try again.`,
            }
        }

        const unknownHeaders = fileHeaders.filter(header => {
            const trimmed = header.trim()

            if (!trimmed) {
                return false
            }

            return !this.csvHeaderToDtoKey(trimmed)
        })

        if (unknownHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: `Unrecognized column(s): ${unknownHeaders.join(", ")}.`,
            }
        }

        const rowCount = await this.countCsvDataRows(file)

        if (rowCount < 1) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: "The file does not contain any data rows.",
            }
        }

        return {
            isValid: true,
            headers: fileHeaders,
            rowCount,
        }
    }

    /**
     * For .txt files, peek at the start to distinguish X12 835 from CSV content.
     */
    private static async detectTxtFileFormat(
        file: File,
        isCsvUploadEnabled: boolean,
    ): Promise<OneVerifyEraUploadFileFormat | null> {
        const peek = await file.slice(0, 256).text()

        const trimmed = peek.replace(/^\uFEFF/, "").trimStart()

        const { isa } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        if (trimmed.startsWith(isa)) {
            return OneVerifyEraUploadFileFormat.x12
        }

        if (!isCsvUploadEnabled) {
            return null
        }

        const csvValidation = await this.validateCsvFile(file)

        if (csvValidation.isValid) {
            return OneVerifyEraUploadFileFormat.csv
        }

        return null
    }

    /**
     * Stream-parse a CSV file and collect up to maxRows review DTOs.
     */
    private static parseCsvPreviewFromFile(
        file: File,
        maxRows: number,
    ): Promise<RawEraUploadCsvRowDTO[]> {
        return new Promise((resolve, reject) => {
            const rows: RawEraUploadCsvRowDTO[] = []

            PapaParse.parse<Record<string, string>>(file, {
                header: true,
                skipEmptyLines: true,
                step: (results, parser) => {
                    const dto = this.mapParsedCsvRowToDto(
                        results.data,
                        rows.length,
                    )

                    if (!dto) {
                        return
                    }

                    rows.push(dto)

                    if (rows.length >= maxRows) {
                        parser.abort()
                    }
                },
                complete: () => {
                    resolve(rows)
                },
                error: error => {
                    reject(error)
                },
            })
        })
    }

    /**
     * Map a PapaParse row object to a review DTO, or null when the row should be skipped.
     */
    private static mapParsedCsvRowToDto(
        row: Record<string, string>,
        index: number,
    ): RawEraUploadCsvRowDTO | null {
        if (this.shouldSkipParsedCsvRow(row)) {
            return null
        }

        const raw: Record<string, string> = {}

        for (const [header, value] of Object.entries(row)) {
            const trimmedHeader = header.trim()

            const key = this.csvHeaderToDtoKey(trimmedHeader)

            if (key === undefined || value === undefined) {
                continue
            }

            raw[key] = String(value).trim()
        }

        if (this.isEmptyCsvRow(raw)) {
            return null
        }

        const dto = plainToInstance(RawEraUploadCsvRowDTO, raw)

        dto.index = index

        return dto
    }

    /**
     * Skip blank, comment, and comma-only rows before mapping to DTO fields.
     */
    private static shouldSkipParsedCsvRow(
        row: Record<string, string>,
    ): boolean {
        const values = Object.values(row).map(value => String(value ?? "").trim())

        const nonEmptyValues = values.filter(Boolean)

        if (nonEmptyValues.length === 0) {
            return true
        }

        if (
            nonEmptyValues.length === 1 &&
            nonEmptyValues[0].startsWith("#")
        ) {
            return true
        }

        if (values.every(value => /^[\s,]*$/.test(value))) {
            return true
        }

        const withoutQuotedEmpties = values
            .map(value => value.replace(/"\s*"/g, ""))
            .join(",")

        return /^[\s,]*$/.test(withoutQuotedEmpties)
    }

    /**
     * Lowercase extension from a file name, or undefined when absent.
     */
    private static getFileExtension(fileName: string): string | undefined {
        if (!fileName.includes(".")) {
            return undefined
        }

        return fileName.split(".").pop()?.toLowerCase()
    }

    /**
     * Parse the first line of CSV text into trimmed header names.
     */
    private static readCsvHeadersFromText(previewText: string): string[] {
        const firstLine = previewText.split(/\r?\n/)[0] ?? ""

        const parsed = PapaParse.parse<string[]>(firstLine, {
            header: false,
            skipEmptyLines: true,
        })

        return (parsed.data[0] ?? []).map(header => header.trim())
    }

    /**
     * Read column headers from the start of a file without loading the full contents.
     */
    private static async readCsvFileHeaders(file: File): Promise<string[]> {
        const previewBlob = file.slice(
            0,
            INTEGRATIONS_CONFIG.eraUpload.maxHeaderPreviewBytes,
        )

        const previewText = await previewBlob.text()

        return this.readCsvHeadersFromText(previewText)
    }

    /**
     * Template headers that are missing from the uploaded file.
     */
    private static getMissingRequiredCsvHeaders(
        fileHeaders: string[],
    ): string[] {
        const headerSet = new Set(fileHeaders.map(header => header.trim()))

        return Object.keys(
            INTEGRATIONS_CONFIG.eraUpload.ruleDisplayTableColumnMapping,
        )
            .map(key => UtilHelper.camelCaseToSnakeCase(key))
            .filter(requiredHeader => !headerSet.has(requiredHeader))
    }

    /**
     * Map a CSV header to a DTO key when it matches a known ERA upload column.
     */
    private static csvHeaderToDtoKey(
        header: string,
    ): keyof RawEraUploadCsvRowDTO | undefined {
        const key = UtilHelper.snakeCaseToCamelCase(header.trim())

        if (this.isKnownEraUploadCsvDtoKey(key)) {
            return key
        }

        return undefined
    }

    /**
     * Whether a camelCase key corresponds to an ERA upload CSV column.
     */
    private static isKnownEraUploadCsvDtoKey(
        key: string,
    ): key is keyof RawEraUploadCsvRowDTO {
        return Object.hasOwn(
            INTEGRATIONS_CONFIG.eraUpload.ruleDisplayTableColumnMapping,
            key,
        )
    }

    /**
     * Count data rows by streaming newlines (header row excluded). Used for large files.
     */
    private static async countCsvDataRows(file: File): Promise<number> {
        const reader = file.stream().getReader()

        const decoder = new TextDecoder()

        let newlineCount = 0

        while (true) {
            const { done, value } = await reader.read()

            if (done) {
                break
            }

            const chunk = decoder.decode(value, { stream: true })

            const matches = chunk.match(/\n/g)

            if (matches) {
                newlineCount += matches.length
            }
        }

        if (newlineCount === 0) {
            return 0
        }

        const dataRows = newlineCount - 1

        const endsWithNewline = await this.fileEndsWithNewline(file)

        return endsWithNewline ? dataRows : dataRows + 1
    }

    /**
     * Whether the file ends with a newline, for accurate row counting on the last line.
     */
    private static async fileEndsWithNewline(file: File): Promise<boolean> {
        if (file.size === 0) {
            return false
        }

        const tailBlob = file.slice(Math.max(0, file.size - 2), file.size)

        const tailText = await tailBlob.text()

        return tailText.endsWith("\n") || tailText.endsWith("\r")
    }

    /**
     * True when every cell in a parsed row is empty or whitespace.
     */
    private static isEmptyCsvRow(row: Record<string, unknown>): boolean {
        return Object.values(row).every(v => /^[\s,]*$/.test(String(v ?? "")))
    }

    /**
     * Validate that the file start contains ISA, GS, and ST segments (835 structure check).
     */
    private static async validateX12Structure(
        file: File,
    ): Promise<IEraUploadX12ValidationResult> {
        const previewBlob = file.slice(
            0,
            Math.min(
                file.size,
                INTEGRATIONS_CONFIG.eraUpload.maxX12StructurePreviewBytes,
            ),
        )
        const previewText = await previewBlob.text()

        const segments = this.getRequiredX12SegmentPresence(previewText)

        const { isa, gs, st } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        const missingSegments = [
            !segments.isa && isa,
            !segments.gs && gs,
            !segments.st && st,
        ].filter(Boolean) as string[]

        if (missingSegments.length) {
            return {
                isValid: false,
                segments,
                errorMessage: `Missing required X12 segment(s): ${missingSegments.join(", ")}. Confirm this is a valid 835 remittance file.`,
            }
        }

        return {
            isValid: true,
            segments,
        }
    }

    /**
     * Whether ISA, GS, and ST segment IDs appear in the parsed preview text.
     */
    private static getRequiredX12SegmentPresence(
        text: string,
    ): IEraUploadX12SegmentPresence {
        const segmentIds = this.getX12SegmentIds(text)

        const { isa, gs, st } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        return {
            isa: segmentIds.includes(isa),
            gs: segmentIds.includes(gs),
            st: segmentIds.includes(st),
        }
    }

    /**
     * Extract X12 segment IDs from a preview using ISA element and segment terminators.
     */
    private static getX12SegmentIds(text: string): string[] {
        const cleanText = text.replace(/^\uFEFF/, "").trimStart()

        const { isa } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        if (!cleanText.startsWith(isa) || cleanText.length < 106) {
            return []
        }

        const elementSeparator = cleanText.charAt(3)

        const segmentTerminator = cleanText.charAt(105)

        return cleanText
            .split(segmentTerminator)
            .map(segment => segment.replace(/[\r\n]/g, "").trim())
            .filter(Boolean)
            .map(segment => segment.split(elementSeparator)[0]?.trim() ?? "")
            .filter(Boolean)
    }
}
